/**
 * "Most recent" Time Period sentinel.
 * Persist this label in save selection; resolve to the latest concrete
 * period from the current time-period list at submit / load / export time.
 */

export const MOST_RECENT_TIME_PERIOD_LABEL = 'Most recent';

/** Level1 granularities that support Most recent (UI shows Rolling Ave for MMA). */
export const MOST_RECENT_TIME_PERIOD_TYPES: readonly string[] = [
  'Month',
  'Quarter',
  'Year',
  '3MMA',
  '4MMA',
  '6MMA',
  '12MMA',
  'Week',
];

export function isMostRecentTimePeriodLabel(
  value: string | null | undefined,
): boolean {
  return (
    (value || '').trim().toLowerCase() ===
    MOST_RECENT_TIME_PERIOD_LABEL.toLowerCase()
  );
}

export function isMostRecentTimePeriodType(
  level1: string | null | undefined,
): boolean {
  return MOST_RECENT_TIME_PERIOD_TYPES.includes((level1 || '').trim());
}

/** True if Child is like `Month|Most recent`. */
export function isMostRecentTimePeriodChild(
  child: string | null | undefined,
): boolean {
  if (!child) return false;
  const parts = String(child).split('|');
  return isMostRecentTimePeriodLabel(parts[parts.length - 1]);
}

/**
 * Selection-summary display: Most recent (June_2026)
 */
export function formatMostRecentSelectionLabel(
  concretePeriodLabel: string | null | undefined,
): string {
  const concrete = (concretePeriodLabel || '').trim();
  if (!concrete || isMostRecentTimePeriodLabel(concrete)) {
    return MOST_RECENT_TIME_PERIOD_LABEL;
  }
  // Already formatted
  if (/^most recent\s*\(/i.test(concrete)) {
    return concrete;
  }
  return `${MOST_RECENT_TIME_PERIOD_LABEL} (${concrete})`;
}

/**
 * Formats a pipe Child like Month|June_2026 for summary when it was a Most recent pick:
 * Month|Most recent (June_2026)  (transformData later turns | into : )
 */
export function formatMostRecentChildForSummary(
  child: string | null | undefined,
): string {
  if (!child) return '';
  const parts = String(child).split('|').map((p) => p.trim());
  if (parts.length < 2) {
    return formatMostRecentSelectionLabel(parts[0]);
  }
  const gran = parts[0];
  const concrete = parts[parts.length - 1];
  return `${gran}|${formatMostRecentSelectionLabel(concrete)}`;
}

/**
 * Snapshot Level2: inject Most recent as first option (after latest-first sort).
 * Clones codes from the current latest concrete period for attribute binding;
 * submit must still re-resolve from the live list.
 */
export function injectMostRecentIntoSnapshotLevel2(level1Node: any): void {
  if (
    !level1Node ||
    !isMostRecentTimePeriodType(level1Node.Level1_value) ||
    !Array.isArray(level1Node.Level2)
  ) {
    return;
  }

  const concrete = level1Node.Level2.filter(
    (l2: any) => !isMostRecentTimePeriodLabel(l2?.Level2_value),
  );
  if (!concrete.length) {
    level1Node.Level2 = concrete;
    return;
  }

  const latest = concrete[0];
  const mostRecent = {
    ...latest,
    Level2_value: MOST_RECENT_TIME_PERIOD_LABEL,
    isMostRecent: true,
    tooltip: MOST_RECENT_TIME_PERIOD_LABEL,
  };
  level1Node.Level2 = [mostRecent, ...concrete];
}

/**
 * Resolve a Snapshot Time Period selection item that uses Most recent
 * against the current variableList (latest-first concrete Level2).
 */
export function resolveSnapshotMostRecentSelection(
  selectionItem: any,
  variableList: any[],
): any {
  if (!selectionItem) {
    return selectionItem;
  }
  const isTp =
    selectionItem.Level0 === 'Time Period' ||
    isMostRecentTimePeriodType(selectionItem.Level1);
  if (!isTp) {
    return selectionItem;
  }
  if (
    !isMostRecentTimePeriodLabel(selectionItem.Level2) &&
    !selectionItem.isMostRecent
  ) {
    return selectionItem;
  }

  const tp = (variableList || []).find((v) => v.Level0 === 'Time Period');
  const l1 =
    tp?.Level1?.find((x: any) => x.Level1_value === selectionItem.Level1) ||
    tp?.Level1?.[0];
  const concrete = (l1?.Level2 || []).find(
    (l2: any) => !isMostRecentTimePeriodLabel(l2?.Level2_value),
  );
  if (!concrete) {
    return selectionItem;
  }

  return {
    ...selectionItem,
    Level0: selectionItem.Level0 || 'Time Period',
    Level1: selectionItem.Level1 || l1?.Level1_value,
    Level2: concrete.Level2_value,
    response_code: concrete.response_code ?? selectionItem.response_code,
    year_code: concrete.year_code ?? selectionItem.year_code,
    date: concrete.date ?? selectionItem.date,
    sort_id: concrete.sort_id ?? selectionItem.sort_id,
    variable_code:
      concrete.data_variable_code ??
      concrete.variable_code ??
      selectionItem.variable_code,
    dataYearCode: concrete.dataYearCode ?? selectionItem.dataYearCode,
    Weekno_forSigcalculation:
      concrete.Weekno_forSigcalculation ??
      selectionItem.Weekno_forSigcalculation,
    isMostRecent: false,
    isMostRecentResolved: true,
    mostRecentSourceLabel: MOST_RECENT_TIME_PERIOD_LABEL,
  };
}

/** Child key used for non-trend output filtering, e.g. Month|June_2026. */
export function getResolvedTimePeriodChildKey(
  selectionItem: any,
  variableList: any[],
): string {
  const resolved = resolveSnapshotMostRecentSelection(
    selectionItem,
    variableList,
  );
  if (!resolved) {
    return '';
  }
  return `${resolved.Level1}|${resolved.Level2}`;
}

/**
 * Inject synthetic flat FOM-style rows for CT/BA variableTabData.
 * MetricOption = "Most recent"; copies codes from the latest concrete row per Level1.
 */
export function injectMostRecentIntoVariableTabData(rows: any[]): any[] {
  if (!Array.isArray(rows) || !rows.length) {
    return rows;
  }

  const withoutPrior = rows.filter(
    (r) =>
      !(
        r?.Level0 === 'Time Period' &&
        isMostRecentTimePeriodLabel(r?.MetricOption)
      ),
  );

  const byGranularity = new Map<string, any[]>();
  for (const row of withoutPrior) {
    if (row?.Level0 !== 'Time Period' || !row.Level1) {
      continue;
    }
    if (!isMostRecentTimePeriodType(row.Level1)) {
      continue;
    }
    const key = String(row.Level1).trim();
    if (!byGranularity.has(key)) {
      byGranularity.set(key, []);
    }
    byGranularity.get(key)!.push(row);
  }

  const synthetics: any[] = [];
  byGranularity.forEach((groupRows, gran) => {
    const sorted = [...groupRows].sort((a, b) => {
      const da = a?.date ? new Date(a.date).getTime() : 0;
      const db = b?.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });
    const latest = sorted[0];
    if (!latest) {
      return;
    }
    synthetics.push({
      ...latest,
      MetricOption: MOST_RECENT_TIME_PERIOD_LABEL,
      Level2: '',
      response_label: MOST_RECENT_TIME_PERIOD_LABEL,
      tooltip: MOST_RECENT_TIME_PERIOD_LABEL,
      isMostRecent: true,
      sort_id: String(Number(latest.sort_id || 0) - 1),
    });
  });

  if (!synthetics.length) {
    return withoutPrior;
  }
  return [...synthetics, ...withoutPrior];
}

/**
 * Resolve CT/BA selection Data item when Child ends with Most recent.
 * Uses current variableTabData for that Level0/Level1.
 */
export function resolveCrosstabMostRecentDataItem(
  dataItem: any,
  parent: string,
  variableTabData: any[],
): any {
  if (!dataItem?.isTimePeriod && parent !== 'Time Period') {
    // still check Child sentinel
  }
  if (
    !isMostRecentTimePeriodChild(dataItem?.Child) &&
    !dataItem?.isMostRecent
  ) {
    return dataItem;
  }

  const parts = String(dataItem.Child || '').split('|');
  const gran = parts[0] || parent;
  const candidates = (variableTabData || []).filter(
    (r) =>
      r?.Level0 === 'Time Period' &&
      r?.Level1 === gran &&
      r?.MetricOption &&
      !isMostRecentTimePeriodLabel(r.MetricOption),
  );
  const sorted = [...candidates].sort((a, b) => {
    const da = a?.date ? new Date(a.date).getTime() : 0;
    const db = b?.date ? new Date(b.date).getTime() : 0;
    return db - da;
  });
  const latest = sorted[0];
  if (!latest) {
    return dataItem;
  }

  return {
    ...dataItem,
    Child: `${gran}|${latest.MetricOption}`,
    responsecode: latest.response_code ?? dataItem.responsecode,
    ResponseCode: latest.response_code ?? dataItem.ResponseCode,
    year_response_code: latest.year_code ?? dataItem.year_response_code,
    Date: latest.date ?? dataItem.Date,
    date: latest.date ?? dataItem.date,
    sort_id: latest.sort_id ?? dataItem.sort_id,
    variablecode: latest.data_variable_code ?? dataItem.variablecode,
    year_variable_code: latest.dataYearCode ?? dataItem.year_variable_code,
    isMostRecentResolved: true,
    mostRecentSourceLabel: MOST_RECENT_TIME_PERIOD_LABEL,
  };
}
